/**
 * Shared per-slot inner-border helpers used by every output path:
 *   - ejsGenerator.js  → string-form CSS for the exported EJS template
 *   - previewGenerator.js → string-form CSS for the live preview iframe
 *   - renderers.jsx    → React style object for the designer canvas
 *
 * All three formats are computed from the same per-side {top,bottom,left,right}
 * + {style,color} schema so the canvas, preview, and export stay aligned.
 *
 * Box-sizing: the wrapping <div>s already hard-code `box-sizing: border-box`
 * (via `!important` in the EJS/preview emitters), so these helpers only emit
 * the per-side border declarations themselves. `slotBorderStyle` includes
 * `boxSizing: "border-box"` since React inline-styles need it explicitly.
 */

/**
 * Compute the four per-side border declarations as an object keyed by CSS
 * property name. Returns `null` if no side has a non-zero width — callers can
 * short-circuit emitting border markup in that case.
 */
function computeBorderSides(eb) {
  if (!eb) return null;
  const style = eb.style || "solid";
  const color = eb.color || "#000000";
  const out = {};
  if (eb.top)    out["border-top"]    = `${eb.top}px ${style} ${color}`;
  if (eb.bottom) out["border-bottom"] = `${eb.bottom}px ${style} ${color}`;
  if (eb.left)   out["border-left"]   = `${eb.left}px ${style} ${color}`;
  if (eb.right)  out["border-right"]  = `${eb.right}px ${style} ${color}`;
  return Object.keys(out).length === 0 ? null : out;
}

/** CSS string, terminated by `;` for direct interpolation into a style attr. */
export function slotBorderCss(eb) {
  const sides = computeBorderSides(eb);
  if (!sides) return "";
  return Object.entries(sides).map(([k, v]) => `${k}: ${v}`).join("; ") + ";";
}

/** React style object (camelCased keys) including `boxSizing: "border-box"`. */
export function slotBorderStyle(eb) {
  const sides = computeBorderSides(eb);
  if (!sides) return null;
  return {
    borderTop:    sides["border-top"],
    borderBottom: sides["border-bottom"],
    borderLeft:   sides["border-left"],
    borderRight:  sides["border-right"],
    boxSizing:    "border-box",
  };
}
